import { FunctionComponent, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
    GetTicketsSearchParams,
    TicketsContainer,
    B2BUser,
    convertGetTicketsSearchParamsToBackend,
} from '@starjack/entities/ticket';
import { useGetLiftOperatorsQuery } from '@starjack/entities/liftoperator';
import { useNotification } from '@starjack/features/notifications';
import { fetchAuthWrapper } from '@starjack/api';
import { urlBuilder, buildSearchParams } from '@starjack/utils';
import { DateRangePicker } from '@starjack/shared-components';

const downloadContentGeneric = (filename: string, content: string) => {
    const element = document.createElement('a');
    element.setAttribute('href', content);
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

const isValidDate = (d: Date | null): d is Date =>
    d instanceof Date && !isNaN(d.getTime());

const parseDateFromUrl = (dateString: string | null): Date | undefined => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    return isValidDate(date) ? date : undefined;
};

export const TicketsContent: FunctionComponent = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { addNotification } = useNotification();

    // Parse URL search params for initial state
    const initialParams: GetTicketsSearchParams = useMemo(() => {
        const userId = searchParams.get('userId') || undefined;
        const orderId = searchParams.get('orderId') || undefined;
        const query = searchParams.get('query') || undefined;
        const orderDateFrom = parseDateFromUrl(
            searchParams.get('orderDateFrom')
        );
        const orderDateTo = parseDateFromUrl(searchParams.get('orderDateTo'));
        const ticketStartFrom = parseDateFromUrl(
            searchParams.get('ticketStartFrom')
        );
        const ticketStartTo = parseDateFromUrl(
            searchParams.get('ticketStartTo')
        );
        const page = parseInt(searchParams.get('page') || '0', 10);
        const size = parseInt(searchParams.get('size') || '25', 10);

        return {
            userId,
            orderId,
            query,
            orderDateFrom,
            orderDateTo,
            ticketStartFrom,
            ticketStartTo,
            page,
            size,
            sort: 'orderDate,desc',
            hasInsurance: false,
        };
    }, [searchParams]);

    const { data: liftOperators } = useGetLiftOperatorsQuery();

    // B2B users are app-specific, so we'll make them optional
    // Apps can extend this widget if needed, but for now we'll work without them
    const b2bUsers: B2BUser[] = [];

    const handleExport = useCallback(
        async (params: GetTicketsSearchParams) => {
            try {
                const url = urlBuilder(
                    'order',
                    'liftoperators/tickets-paged.xlsx'
                );
                const response = await fetchAuthWrapper(
                    `${url}${buildSearchParams(
                        convertGetTicketsSearchParamsToBackend(params)
                    )}`,
                    {
                        method: 'GET',
                        headers: {
                            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        },
                    }
                );

                if (!response.ok) {
                    addNotification({
                        type: 'error',
                        message:
                            t('ticketsPage.exportError') ||
                            'Fehler beim Export',
                    });
                    return null;
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                downloadContentGeneric(
                    `${t('ticketsPage.tickets')} - ${format(
                        new Date(),
                        'yyyy-MM-dd_HH-mm-ss'
                    )}.xlsx`,
                    blobUrl
                );
                return blob;
            } catch {
                addNotification({
                    type: 'error',
                    message:
                        t('ticketsPage.exportError') || 'Fehler beim Export',
                });
                return null;
            }
        },
        [addNotification, t]
    );

    return (
        <TicketsContainer
            initialParams={initialParams}
            basePath='/tickets'
            navigate={navigate}
            onExport={handleExport}
            liftOperators={liftOperators}
            b2bUsers={b2bUsers}
            locale={i18n.language}
            translations={{
                de: i18n.store.data.de?.translation as Record<string, string>,
                en: i18n.store.data.en?.translation as Record<string, string>,
            }}
            t={t}
            hideColumns={['liftOperator']}
            hideDetailFields={['liftOperator']}
            renderOrderDateRange={({ value, onChange, fromText, toText }) => (
                <DateRangePicker
                    value={value}
                    onChange={onChange}
                    fromText={fromText}
                    toText={toText}
                />
            )}
            renderTicketStartDateRange={({
                value,
                onChange,
                fromText,
                toText,
            }) => (
                <DateRangePicker
                    value={value}
                    onChange={onChange}
                    fromText={fromText}
                    toText={toText}
                />
            )}
        />
    );
};
